import moment from 'moment';
import api from '@/common/api';
import { utcToLocalDate } from '@/common/calendarTime';

// Публичная (гостевая) сторона календаря записи — без авторизации, отдельно
// от админского src/store/calendar/calendar.js. См. audit/calendar-api-reference.md,
// раздел «Публичная сторона — флоу гостя».

const initialState = () => ({
  loading: false,
  error: [],
  options: [], // AvailabilityOption[] — плоский список слотов от GET /calendar/availability/

  // Отметки "в этот день у кого-то есть свободное время" для месячной сетки календаря.
  // date у /calendar/availability/ теперь необязателен (обновлено на бэке 04.09.2026): без него
  // отдаются все текущие незавершённые и будущие интервалы одним запросом — раскладываем их по
  // датам сами и оставляем только то, что попадает в отображаемый месяц.
  monthAvailability: {
    loading: false,
    dates: [], // string[] в формате YYYY-MM-DD
  },

  booking: {
    loading: false,
    error: [],
    result: null, // ответ POST /calendar/appointments/ после успешной брони
  },

  cancellation: {
    loading: false,
    error: [],
    done: false,
    conflict: false, // 409 — бронь уже неактуальна
  },
});

export default {
  namespaced: true,
  state: initialState,
  actions: {
    async searchAvailability({ commit }, { date, expert, serviceType }) {
      commit('SET_LOADING', true);
      commit('SET_ERROR', []);
      try {
        const params = { date };
        if (expert) params.expert = expert;
        if (serviceType) params.service_type = serviceType;
        const response = await api.get('/calendar/availability/', { params });
        commit('SET_OPTIONS', Array.isArray(response) ? response : []);
      } catch (err) {
        commit('SET_ERROR', Object.entries(err.data || {}));
        commit('SET_OPTIONS', []);
        throw err;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    resetOptions({ commit }) {
      commit('SET_OPTIONS', []);
    },

    async loadMonthAvailability({ commit }, month) {
      commit('SET_MONTH_LOADING', true);
      try {
        const monthStart = moment(month, 'YYYY-MM').startOf('month');
        const monthEnd = moment(month, 'YYYY-MM').endOf('month');
        const response = await api.get('/calendar/availability/');
        const dates = new Set();
        (Array.isArray(response) ? response : []).forEach((option) => {
          const date = utcToLocalDate(option.starts_at);
          if (moment(date, 'YYYY-MM-DD').isBetween(monthStart, monthEnd, 'day', '[]')) {
            dates.add(date);
          }
        });
        commit('SET_MONTH_AVAILABILITY', [...dates]);
      } catch {
        commit('SET_MONTH_AVAILABILITY', []);
      } finally {
        commit('SET_MONTH_LOADING', false);
      }
    },

    async createAppointment({ commit }, payload) {
      commit('SET_BOOKING_LOADING', true);
      commit('SET_BOOKING_ERROR', []);
      try {
        const response = await api.post('/calendar/appointments/', payload);
        commit('SET_BOOKING_RESULT', response);
        return response;
      } catch (err) {
        commit('SET_BOOKING_ERROR', Object.entries(err.data || {}));
        throw err;
      } finally {
        commit('SET_BOOKING_LOADING', false);
      }
    },

    resetBooking({ commit }) {
      commit('SET_BOOKING_RESULT', null);
      commit('SET_BOOKING_ERROR', []);
    },

    async cancelAppointment({ commit }, token) {
      commit('SET_CANCEL_LOADING', true);
      commit('SET_CANCEL_ERROR', { value: [], conflict: false });
      try {
        await api.post('/calendar/appointments/cancel/', { token });
        commit('SET_CANCEL_DONE', true);
      } catch (err) {
        if (err.status === 409) {
          commit('SET_CANCEL_ERROR', { value: [], conflict: true });
        } else {
          commit('SET_CANCEL_ERROR', { value: Object.entries(err.data || {}), conflict: false });
        }
        throw err;
      } finally {
        commit('SET_CANCEL_LOADING', false);
      }
    },
  },
  mutations: {
    SET_LOADING(state, value) {
      state.loading = value;
    },
    SET_ERROR(state, value) {
      state.error = value;
    },
    SET_OPTIONS(state, options) {
      state.options = options;
    },
    SET_MONTH_LOADING(state, value) {
      state.monthAvailability.loading = value;
    },
    SET_MONTH_AVAILABILITY(state, dates) {
      state.monthAvailability.dates = dates;
    },
    SET_BOOKING_LOADING(state, value) {
      state.booking.loading = value;
    },
    SET_BOOKING_ERROR(state, value) {
      state.booking.error = value;
    },
    SET_BOOKING_RESULT(state, result) {
      state.booking.result = result;
    },
    SET_CANCEL_LOADING(state, value) {
      state.cancellation.loading = value;
    },
    SET_CANCEL_ERROR(state, { value, conflict }) {
      state.cancellation.error = value;
      state.cancellation.conflict = conflict;
    },
    SET_CANCEL_DONE(state, value) {
      state.cancellation.done = value;
    },
  },
  getters: {
    loading: (state) => state.loading,
    error: (state) => state.error,
    options: (state) => state.options,
    booking: (state) => state.booking,
    cancellation: (state) => state.cancellation,
    monthAvailabilityDates: (state) => state.monthAvailability.dates,
    monthAvailabilityLoading: (state) => state.monthAvailability.loading,

    // Группировка плоского списка AvailabilityOption по эксперту, а внутри — по типу услуги
    // (у одного эксперта на одну дату может быть открыто несколько разных услуг, и без
    // подписи "какая это услуга" список времени было не отличить одно от другого).
    optionsByExpert: (state) => {
      const groups = [];
      const indexByExpertId = {};
      state.options.forEach((option) => {
        const expertId = option.expert && option.expert.id;
        if (indexByExpertId[expertId] === undefined) {
          indexByExpertId[expertId] = groups.length;
          groups.push({ expert: option.expert, services: [], indexByServiceTypeId: {} });
        }
        const group = groups[indexByExpertId[expertId]];
        const serviceTypeId = option.service_type && option.service_type.id;
        if (group.indexByServiceTypeId[serviceTypeId] === undefined) {
          group.indexByServiceTypeId[serviceTypeId] = group.services.length;
          group.services.push({ serviceType: option.service_type, slots: [] });
        }
        group.services[group.indexByServiceTypeId[serviceTypeId]].slots.push(option);
      });
      return groups.map(({ expert, services }) => ({ expert, services }));
    },
  },
};
